import { useEffect, useMemo, useState } from "react";
import { Close } from "../icons";

export default function FeedbackModal({
    isOpen,
    targetMessage,
    isSubmitting = false,
    onClose,
    onSubmit,
}) {
    const [feedbackType, setFeedbackType] = useState("");
    const [feedbackText, setFeedbackText] = useState("");
    const [errorText, setErrorText] = useState("");

    useEffect(() => {
        if (!isOpen) return;

        setFeedbackType("");
        setFeedbackText("");
        setErrorText("");
    }, [isOpen, targetMessage?.id]);

    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (event) => {
            if (event.key === "Escape" && !isSubmitting) {
                onClose?.();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, isSubmitting, onClose]);

    const previewText = useMemo(() => {
        const text = String(targetMessage?.text || "").trim();
        if (!text) return "No message preview available.";
        return text.length > 280 ? `${text.slice(0, 280)}…` : text;
    }, [targetMessage?.text]);

    if (!isOpen) return null;

    const handleSubmit = async () => {
        const normalizedType = String(feedbackType || "").trim().toLowerCase();
        const trimmedText = String(feedbackText || "").trim();

        if (!normalizedType) {
            setErrorText("Please select thumbs up or thumbs down.");
            return;
        }

        if (normalizedType === "negative" && !trimmedText) {
            setErrorText("Text feedback is required for thumbs down.");
            return;
        }

        try {
            setErrorText("");
            await onSubmit?.({
                feedbackType: normalizedType,
                feedbackText: trimmedText,
            });
        } catch (error) {
            setErrorText(error?.message || "Feedback could not be sent. Please try again.");
        }
    };

    return (
        <div
            className="werner-feedbackModal-overlay"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget && !isSubmitting) {
                    onClose?.();
                }
            }}
        >
            <div
                className="werner-feedbackModal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="werner-feedbackModal-title"
            >
                <div className="werner-feedbackModal-header">
                    <div>
                        <h3 id="werner-feedbackModal-title" className="werner-feedbackModal-title">
                            Send feedback
                        </h3>
                        <p className="werner-feedbackModal-subtitle">
                            Choose thumbs up or thumbs down and optionally add a short message.
                        </p>
                    </div>

                    <button
                        type="button"
                        className="werner-feedbackModal-close"
                        onClick={() => onClose?.()}
                        disabled={isSubmitting}
                        aria-label="Close feedback modal"
                        title="Close"
                    >
                        <Close className="werner-feedbackModal-closeIcon" />
                    </button>
                </div>

                <div className="werner-feedbackModal-section">
                    <div className="werner-feedbackModal-label">AI message</div>
                    <div className="werner-feedbackModal-preview">{previewText}</div>
                </div>

                <div className="werner-feedbackModal-section">
                    <div className="werner-feedbackModal-label">Feedback type</div>

                    <div className="werner-feedbackModal-choices">
                        <button
                            type="button"
                            className={`werner-feedbackModal-choice werner-feedbackModal-choice--positive ${feedbackType === "positive" ? "is-active" : ""
                                }`}
                            onClick={() => {
                                setFeedbackType("positive");
                                setErrorText("");
                            }}
                            disabled={isSubmitting}
                        >
                            <span className="werner-feedbackModal-choiceEmoji" aria-hidden="true">
                                👍
                            </span>
                            <span>Thumbs up</span>
                        </button>

                        <button
                            type="button"
                            className={`werner-feedbackModal-choice werner-feedbackModal-choice--negative ${feedbackType === "negative" ? "is-active" : ""
                                }`}
                            onClick={() => {
                                setFeedbackType("negative");
                                setErrorText("");
                            }}
                            disabled={isSubmitting}
                        >
                            <span className="werner-feedbackModal-choiceEmoji" aria-hidden="true">
                                👎
                            </span>
                            <span>Thumbs down</span>
                        </button>
                    </div>
                </div>

                <div className="werner-feedbackModal-section">
                    <label className="werner-feedbackModal-label" htmlFor="werner-feedback-textarea">
                        Message {feedbackType === "negative" ? "(required)" : "(optional)"}
                    </label>

                    <textarea
                        id="werner-feedback-textarea"
                        className="werner-feedbackModal-textarea"
                        rows={5}
                        value={feedbackText}
                        onChange={(event) => {
                            setFeedbackText(event.target.value);
                            if (errorText) setErrorText("");
                        }}
                        placeholder="Tell us what was helpful or what should be improved..."
                        disabled={isSubmitting}
                    />
                </div>

                <div className="werner-feedbackModal-info">
                    Submitted feedback, the selected AI message, and the whole conversation will be stored.
                </div>

                {errorText && (
                    <div className="werner-feedbackModal-error" role="alert">
                        {errorText}
                    </div>
                )}

                <div className="werner-feedbackModal-actions">
                    <button
                        type="button"
                        className="werner-feedbackModal-cancel"
                        onClick={() => onClose?.()}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        className="werner-feedbackModal-submit"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? "Sending..." : "Send feedback"}
                    </button>
                </div>
            </div>
        </div>
    );
}